import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { UserAvatar } from '../components/UserAvatar'
import { PwaUpdateBanner } from '../components/PwaUpdateBanner'
import { useAuth } from '../hooks/useAuth'
import { useServices } from '../../core/services/ServicesContext'
import logo from '../../assets/logo.svg'
import './AcceptInvite.css'

export function AcceptInvite() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user, authChecked, userDetails, userDetailsLoading, userDetailsError, refreshUserDetails, logout, switchAccount } = useAuth()
  const { serverSync, hydrationCoordinator } = useServices()
  const [status, setStatus] = useState<'loading' | 'verifying-user' | 'verify-error' | 'accepted-loading' | 'success' | 'error' | 'wrong-user'>('loading')
  const [message, setMessage] = useState('')
  const [hasProcessed, setHasProcessed] = useState(false)
  const [showUserDetails, setShowUserDetails] = useState(false)

  const acceptInvite = useCallback(async (token: string, scenarioId: string) => {
    try {
      // Delegate orchestration to infrastructure service
      const returnedScenarioId = await serverSync.acceptInvite(token, scenarioId)

      setStatus('accepted-loading')
      setMessage('Invite accepted. Loading the scenario…')

      // Clear stored token and scenarioId
      sessionStorage.removeItem('pending_invite_token')
      sessionStorage.removeItem('pending_invite_scenario')

      // Hydrate scenario via coordinator (server-first validation)
      await hydrationCoordinator.ensureScenarioReady(returnedScenarioId, 'high')

      navigate(`/scenario/${returnedScenarioId}`)

    } catch (error: unknown) {
      console.error('Error accepting invite:', error)
      
      if ((error as any).code === 'WRONG_USER') {
        setStatus('wrong-user')
        setMessage((error as Error).message)
      } else {
        setStatus('error')
        setMessage(error instanceof Error ? error.message : 'Failed to accept invite')
      }
    }
  }, [navigate, serverSync, hydrationCoordinator])

  useEffect(() => {
    // Wait for auth check to complete
    if (!authChecked) return
    
    // Prevent re-processing
    if (hasProcessed) return

    let token = searchParams.get('token')
    let scenarioId = searchParams.get('scenarioId')
    
    // If not in URL, try to get from sessionStorage (post-login redirect)
    if (!token || !scenarioId) {
      token = sessionStorage.getItem('pending_invite_token')
      scenarioId = sessionStorage.getItem('pending_invite_scenario')
    }
    
    if (!token || !scenarioId) {
      setStatus('error')
      setMessage('Invalid invite link')
      return
    }

    // Store token and scenarioId in case we need to login
    sessionStorage.setItem('pending_invite_token', token)
    sessionStorage.setItem('pending_invite_scenario', scenarioId)
    
    if (!user) {
      // Must not automatically redirect to avoid loops
      // UI will show login prompt
      return
    }

    // Wait for user profile/details to be fetched before accepting invite.
    // This fetch uses /api/auth/user and ensures PostUserProfile has completed.
    if (userDetailsError) {
      setStatus('verify-error')
      setMessage(userDetailsError.message || 'Unable to verify your account')
      return
    }

    if (userDetailsLoading || !userDetails) {
      setStatus('verifying-user')
      setMessage('Verifying your account…')
      return
    }

    // User is authenticated and profile has loaded, try to accept invite
    setHasProcessed(true)
    acceptInvite(token, scenarioId)
  }, [authChecked, user, userDetailsLoading, userDetails, userDetailsError, hasProcessed, searchParams, acceptInvite])

  const handleRetryVerification = () => {
    refreshUserDetails()
  }

  const handleLogin = () => {
    const returnUrl = window.location.pathname + window.location.search
    window.location.assign(
      `/.auth/login/google?post_login_redirect_uri=${encodeURIComponent(returnUrl)}`
    )
  }

  const handleSwitchAccount = switchAccount

  return (
    <div className="app-container invite-page">
      <header className="app-header">
        <div className="header-content">
          <div className="invite-header-left">
            <img src={logo} alt="Ordelio" className="app-logo" />
          </div>
          <div className="invite-title-wrapper">
            <h1 className="invite-title">Invitation</h1>
          </div>
          <div className="header-right">
            <div className="user-section">
              {user && (
                <div className="user-info">
                  <UserAvatar
                    name={userDetails?.name}
                    email={userDetails?.email}
                    displayName={user.userDetails}
                    photoUrl={userDetails?.photoUrl}
                    loading={userDetailsLoading}
                    onClick={() => setShowUserDetails(!showUserDetails)}
                  />
                  {showUserDetails && (
                    <div className="user-details">
                      <span className="user-email">{userDetails?.email || user.userDetails || 'Loading...'}</span>
                      <button onClick={logout} className="logout-btn">Logout</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <PwaUpdateBanner />

      <main className="app-main invite-main">
        <section className="hero-section invite-hero">
          <div className="invite-status-container" role="status" aria-live="polite">
            {!user && authChecked && (
              <>
                <div className="info-icon" aria-hidden="true" style={{ marginBottom: '1rem', fontSize: '3rem' }}>👋</div>
                <h2>You’ve been invited</h2>
                <p>Please sign in to accept this invitation.</p>
                <div className="invite-actions">
                  <button onClick={handleLogin} className="cta-button invite-primary">
                    Sign in with Google
                  </button>
                </div>
              </>
            )}

            {(status === 'loading' || status === 'verifying-user') && user && (
              <>
                <div className="spinner" aria-hidden="true"></div>
                <h2>{status === 'verifying-user' ? 'Verifying user' : 'Accepting invitation'}</h2>
                <p>{message || 'Please wait while we process your invite.'}</p>
                {status === 'verifying-user' && (
                  <div className="invite-actions" style={{ marginTop: '1rem' }}>
                    <button
                      onClick={handleRetryVerification}
                      className="invite-secondary-button"
                      disabled={userDetailsLoading}
                    >
                      Retry verification
                    </button>
                  </div>
                )}
              </>
            )}

            {status === 'verify-error' && user && (
              <>
                <div className="error-icon" aria-hidden="true">✗</div>
                <h2>Unable to verify user</h2>
                <p>{message || 'We could not verify your account right now.'}</p>
                <div className="invite-actions">
                  <button onClick={handleRetryVerification} className="cta-button invite-primary">
                    Retry verification
                  </button>
                  <button onClick={handleSwitchAccount} className="invite-secondary-button">
                    Switch account
                  </button>
                </div>
              </>
            )}

            {status === 'accepted-loading' && (
              <>
                <div className="spinner" aria-hidden="true"></div>
                <h2>Loading scenario</h2>
                <p>{message}</p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="success-icon" aria-hidden="true">✓</div>
                <h2>You’re in</h2>
                <p>{message}</p>
                <p className="redirect-message">Redirecting you to the scenario…</p>
              </>
            )}

            {status === 'wrong-user' && (
              <>
                <div className="error-icon" aria-hidden="true">✗</div>
                <h2>Wrong account</h2>
                <p>{message}</p>
                {user?.userDetails && (
                  <p className="invite-current-user">
                    Currently signed in as <strong>{user.userDetails}</strong>
                  </p>
                )}
                <div className="invite-actions">
                  <button onClick={handleSwitchAccount} className="cta-button invite-primary">
                    Switch account
                  </button>
                  <button onClick={() => navigate('/')} className="invite-secondary-button">
                    Return to Home
                  </button>
                </div>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="error-icon" aria-hidden="true">✗</div>
                <h2>Unable to accept invite</h2>
                <p>{message}</p>
                {user && (
                  <p className="invite-current-user" style={{ marginTop: '0.75rem' }}>
                    Signed in as <strong>{userDetails?.email || user.userDetails}</strong>
                  </p>
                )}
                <div className="invite-actions">
                  {user && (
                    <button onClick={handleSwitchAccount} className="invite-secondary-button">
                      Switch account
                    </button>
                  )}
                  <button onClick={() => navigate('/')} className="cta-button invite-primary">
                    Return to Home
                  </button>
                </div>
              </>
            )}
          </div>
        </section>
      </main>
    </div>
  )
}
